import { Flex, Grid, SVG, SVGs, Text } from '@raskrask-dk/rr-components'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { ProviderOption } from '../../../../details/hooks'
import { CodeInputSwitch } from '../CodeInputSwitch'
import { CodeInputSwitchProps } from '../CodeInputSwitch/props'
import ComplexCodeInputGroup from '../ComplexCodeInputGroup'
import { RedeemedCode } from '../RedeemedCode'
import { RedeemedCodeProps } from '../RedeemedCode/props'
import SimpleCodeInputGroup from '../SimpleCodeInputGroup'
import { CodeValidatorProps, CodeType } from './props'

type ThirdPartyValue = { value: string; label: string } | undefined

export const CodeValidator = ({
    canUseCodeForm,
    canUseValidatorText,

    thirdPartyGiftCardTypes,
    discountAmount,
    discountErrorMessage,
    formatErrorMessage,
    isValid,
    providerOptions,
    codeTypeOptions,
    helpLinkText,

    giftCardHandler,
    packageHandler,
    promoHandler,
    redeemedHandler,

    onCodeRedeem,
    onRedeemedCodeReset,
    presetCode,
}: CodeValidatorProps): ReactElement => {
    const [codeType, setCodeType] = useState<CodeType>()
    const [provider, setProvider] = useState<ProviderOption | undefined>()
    const [code, setCode] = useState<string>('')
    const [activationCode, setActivationCode] = useState<string>()
    const [isThirdPartyValueSelectorShown, setIsThirdPartyValueSelectorShown] =
        useState(false)
    const [thirdPartyValue, setThirdPartyValue] =
        useState<ThirdPartyValue>(undefined)

    const [isError, setIsError] = useState(false) // describes if the given information is actually wrong
    const [isErrorDisplayed, setIsErrorDisplayed] = useState(false) // describes when an error can be displayed regardless if its a wrong case or not
    const [canRedeemCode, setCanRedeemCode] = useState(false)
    const [redeemedCode, setRedeemedCode] = useState<string>()
    const [showCantUseFormError, setShowCantUseFormError] = useState(false)

    // Derived values and functions
    const thirdPartyValueOptions = useMemo(() => {
        if (!provider) return []
        return thirdPartyGiftCardTypes
            .filter((t) => t.provider === provider.value)
            .map((t) => {
                return { label: t.label, value: t.id.toString() }
            })
    }, [provider, thirdPartyGiftCardTypes])

    const isRedeemedCode = redeemedCode !== undefined

    const [helperText, setHelperText] = useState<string>()

    useEffect(() => {
        const getHelperText = () => {
            if (isErrorDisplayed) {
                if (isError) {
                    return formatErrorMessage
                } else {
                    if (isRedeemedCode && discountAmount === 0) {
                        return discountErrorMessage
                    }
                }
            }
            return ''
        }
        setHelperText(getHelperText())
    }, [
        discountAmount,
        discountErrorMessage,
        formatErrorMessage,
        isError,
        isErrorDisplayed,
        isRedeemedCode,
    ])

    const shouldShowHelpLink =
        (provider?.value &&
            provider.value !== 'DenBedsteGave' &&
            provider.value !== 'SendEnTanke') ||
        false

    const helperHref =
        provider?.value && shouldShowHelpLink
            ? `https://www.raskrask.dk/gavekort-guide-${provider.value.toLowerCase()}/`
            : ''

    const codeController = useCallback(
        (
            value: string | undefined,
            activationCode?: string | undefined,
            thirdPartyV?: string | undefined
        ): {
            canShowThirdPartyValueSelector: boolean
            shouldDisplayError: boolean
            canRedeem: boolean
        } => {
            const providerWithFallBack = provider?.value || 'RaskRask'

            const isActivationCodeSet =
                activationCode !== undefined && activationCode !== ''
            const isValueSet = value !== undefined
            const code = isActivationCodeSet
                ? value + ' ' + activationCode
                : value
            const isCodeSet = code !== undefined && code !== ''
            const isValidPattern = !isCodeSet
                ? true
                : codeType === 'promoCode'
                ? true
                : isValid(code, providerWithFallBack)
            const possibleGoDreamID =
                isValueSet &&
                (value.length === 7 ||
                    value.length === 12 ||
                    value.length === 10)
            const possibleGoDreamActivationCode =
                isActivationCodeSet &&
                (activationCode.length === 6 ||
                    activationCode.length === 3 ||
                    activationCode.length === 3)
            const isValidGoDreamCombination =
                (!isActivationCodeSet && possibleGoDreamID) ||
                (!isValueSet && possibleGoDreamActivationCode) ||
                (isValueSet &&
                    isActivationCodeSet &&
                    ((value.length === 7 && activationCode.length === 6) ||
                        (value.length === 12 && activationCode.length === 3) ||
                        (value.length === 10 && activationCode.length === 3)))
            const isValidRR =
                isValidPattern && providerWithFallBack === 'RaskRask'
            const isValidThirdParty =
                isValidPattern && providerWithFallBack !== 'RaskRask'
            const isThirdPartyValueValid =
                providerWithFallBack === 'RaskRask'
                    ? true
                    : thirdPartyV === undefined
                    ? false
                    : true
            return {
                canShowThirdPartyValueSelector: isCodeSet && isValidThirdParty,
                // should only display error if the user gave an input and the pattern is neither valid and neither matching a go dream pattern combination.
                shouldDisplayError:
                    isCodeSet && !isValidPattern && !isValidGoDreamCombination,
                // should only be able to redeem a code if the user gave an input and the pattern valid and a right provider is selected and in case of thrid party giftcard a value is selected as well
                canRedeem:
                    isCodeSet &&
                    (isValidRR || isValidThirdParty) &&
                    isThirdPartyValueValid,
            }
        },
        [isValid, codeType, provider?.value]
    )

    const onClickCodeType = (v: CodeType | undefined) => {
        setIsErrorDisplayed(false)
        setCode('')
        setProvider(undefined)
        setIsThirdPartyValueSelectorShown(false)
        setCanRedeemCode(false)
        if (v === codeType) {
            setCodeType(undefined)
        } else {
            setCodeType(v)
        }
    }

    const onChangeProvider = (p: ProviderOption | undefined) => {
        setIsErrorDisplayed(false)
        setCode('')
        setActivationCode(undefined)
        setIsThirdPartyValueSelectorShown(false)
        setProvider(p || { value: 'RaskRask', label: 'RaskRask' })
    }

    const onChangeCode = useCallback(
        (value: string, activationCode?: string | undefined): void => {
            // validate frontend pattern.
            const codeControl = codeController(value, activationCode)
            setCode(value)
            setActivationCode(activationCode)
            setIsThirdPartyValueSelectorShown(
                codeControl.canShowThirdPartyValueSelector
            )
            setCanRedeemCode(codeControl.canRedeem)
            setIsError(codeControl.shouldDisplayError)
        },
        [codeController]
    )

    const onChangeCodeValueOption = useCallback(
        (
            option:
                | {
                      label: string
                      value: string
                  }
                | undefined
        ) => {
            const codeControl = codeController(
                code,
                activationCode,
                option?.value
            )
            setIsThirdPartyValueSelectorShown(
                codeControl.canShowThirdPartyValueSelector
            )
            setCanRedeemCode(codeControl.canRedeem)
            setIsError(codeControl.shouldDisplayError)
            setThirdPartyValue(option)
        },
        [activationCode, code, codeController]
    )

    const doRedeem = useCallback(() => {
        const thiscode =
            code && activationCode
                ? code + ' ' + activationCode
                : code
                ? code
                : ''
        setRedeemedCode(thiscode)
        onCodeRedeem(
            codeType!,
            provider?.value || 'RaskRask',
            thiscode,
            thirdPartyValue?.value || ''
        )
    }, [
        activationCode,
        code,
        codeType,
        onCodeRedeem,
        provider?.value,
        thirdPartyValue?.value,
    ])

    const onClickRedeem = useCallback(() => {
        // validate frontend pattern again to be sure?  or just use the state value of "canRedeem"?
        const codeControl = codeController(code, activationCode)
        if (codeControl.canRedeem) {
            doRedeem()
        }
    }, [activationCode, doRedeem, code, codeController])

    const doResetRedeem = (): void => {
        setIsError(false)
        setCodeType(undefined)
        setProvider(undefined)
        setIsThirdPartyValueSelectorShown(false)
        setRedeemedCode(undefined)
        onRedeemedCodeReset()
    }

    // Child components
    const GiftCardCodeInputGroup = useMemo(() => {
        return (
            <ComplexCodeInputGroup
                providerListHandler={{
                    defaultProvider: undefined,
                    providerLabel: giftCardHandler.labelForProviderSelector,
                    onChangeProvider: onChangeProvider,
                    providerOptions: providerOptions,
                }}
                codeInputHandler={{
                    code: code,
                    dataCy: 'giftCardCodeInput',
                    helperText: helperText,
                    isError: isError && isErrorDisplayed,
                    isInputFieldDisabled: !provider,
                    onBlur: () => {
                        setIsErrorDisplayed(true)
                    },
                    onChangeCode: (e) => {
                        onChangeCode(e.target.value, activationCode)
                    },
                    placeholder: giftCardHandler.placeholderForCode(provider),
                }}
                activationCodeInputHandler={{
                    activationCodeDataCy: 'activationCodeInput',
                    activationCodePlaceHolder:
                        giftCardHandler.placeholderForActivationCodeInput,
                    isActivationCodeErrorShown: isError && isErrorDisplayed,
                    isActivationCodeFieldShown: provider?.value === 'GoDream',
                    isActivationFieldDisabled: false,
                    onBlurActivationCode: () => {
                        setIsErrorDisplayed(true)
                    },
                    onChangeActivationCode: (e) => {
                        onChangeCode(code, e.target.value)
                    },
                }}
                codeValueListHandler={{
                    codeValueOptions: thirdPartyValueOptions,
                    codeValuePlaceHolder:
                        giftCardHandler.placeholderForCodeValue,
                    defaultCodeValue: undefined,
                    isCodeValueSelectorShown: isThirdPartyValueSelectorShown,
                    onChangeCodeValueOption: onChangeCodeValueOption,
                    selectedOption: thirdPartyValue,
                }}
                ctaHandler={{
                    isButtonDisabled: !canRedeemCode,
                    onClickButton: doRedeem,
                    buttonText: giftCardHandler.textForCtaButton,
                }}
                key={'giftcard'}
            />
        )
    }, [
        activationCode,
        canRedeemCode,
        doRedeem,
        giftCardHandler,
        helperText,
        isError,
        isErrorDisplayed,
        isThirdPartyValueSelectorShown,
        code,
        onChangeCodeValueOption,
        onChangeCode,
        provider,
        providerOptions,
        thirdPartyValue,
        thirdPartyValueOptions,
    ])

    const PackageCodeInputGroup = useMemo(() => {
        return (
            <SimpleCodeInputGroup
                codeInputHandler={{
                    code: code,
                    dataCy: 'packageCodeInput',
                    helperText: helperText,
                    isError: isError && isErrorDisplayed,
                    isInputFieldDisabled: false,
                    onBlur: () => {
                        setIsErrorDisplayed(true)
                    },
                    onChangeCode: (e) => {
                        onChangeCode(e.target.value)
                    },
                    placeholder: packageHandler.placeholderForCode,
                }}
                ctaHandler={{
                    isButtonDisabled: !canRedeemCode,
                    onClickButton: doRedeem,
                    buttonText: packageHandler.textForCtaButton,
                }}
                key={'package'}
            />
        )
    }, [
        canRedeemCode,
        doRedeem,
        helperText,
        isError,
        isErrorDisplayed,
        packageHandler.placeholderForCode,
        packageHandler.textForCtaButton,
        code,
        onChangeCode,
    ])

    const PromoCodeInputGroup = useMemo(() => {
        return (
            <SimpleCodeInputGroup
                codeInputHandler={{
                    code: code,
                    dataCy: 'promoCodeInput',
                    helperText: helperText,
                    isError: isError && isErrorDisplayed,
                    isInputFieldDisabled: false,
                    onBlur: () => {
                        setIsErrorDisplayed(true)
                    },
                    onChangeCode: (e) => {
                        onChangeCode(e.target.value)
                    },
                    placeholder: promoHandler.placeholderForCode,
                }}
                ctaHandler={{
                    isButtonDisabled: !canRedeemCode,
                    onClickButton: onClickRedeem,
                    buttonText: promoHandler.textForCtaButton,
                }}
                key={'promo'}
            />
        )
    }, [
        canRedeemCode,
        helperText,
        isError,
        isErrorDisplayed,
        code,
        onChangeCode,
        onClickRedeem,
        promoHandler.placeholderForCode,
        promoHandler.textForCtaButton,
    ])

    const getCodeValueText = useCallback(() => {
        return (
            codeTypeOptions
                .find((e) => e.value === codeType)
                ?.redeemedCodeText() || ''
        )
    }, [codeTypeOptions, codeType])

    // Subcomponent props
    const redeemedCodeHandler: RedeemedCodeProps = {
        headerText: redeemedHandler.headerText(codeType),
        redeemedCode: redeemedCode,
        codeValueText: getCodeValueText(),
        onClickUndo: () => {
            doResetRedeem()
        },
        hintText: redeemedHandler.hintText,
    }
    const children = {
        giftCardCode: GiftCardCodeInputGroup,
        packageCode: PackageCodeInputGroup,
        promoCode: PromoCodeInputGroup,
    }
    const codeTypeOptionsWithChildren = codeTypeOptions.map((o) => {
        return { ...o, child: children[`${o.value}`] }
    })
    const codeInputSwitchHandler: CodeInputSwitchProps<CodeType | undefined> = {
        helpLinkHandler: {
            href: helperHref,
            linkText: helpLinkText(provider),
            shouldShowHelpLink: shouldShowHelpLink,
        },
        switchHandler: {
            buttonHandlers: codeTypeOptionsWithChildren.map((option, index) => {
                return {
                    isDisabled: !canUseCodeForm,
                    buttonText: option.label,
                    child: option.child,
                    dataCy: `Type-Button-${index}`,
                    value: option.value,
                }
            }),
            selectedOption: codeType,
            onClickButton: onClickCodeType,
            setShowCantUseFormError: setShowCantUseFormError,
            canUseForm: canUseCodeForm,
        },
    }

    useEffect(() => {
        if (presetCode && code === '') {
            setCode(presetCode.code)
            setCodeType(presetCode.codeType)
            onChangeCode(presetCode.code)
        }
    }, [])

    return (
        <>
            {showCantUseFormError && !canUseCodeForm && (
                <Grid gap={4}>
                    {
                        <Flex
                            alignItems="center"
                            css={{ color: '$error600', fontSize: 14 }}
                            gap={4}
                        >
                            <SVG src={SVGs.Exclamation} />
                            <Text>{canUseValidatorText}</Text>
                        </Flex>
                    }
                </Grid>
            )}
            {isRedeemedCode ? (
                <RedeemedCode {...redeemedCodeHandler} />
            ) : (
                <CodeInputSwitch {...codeInputSwitchHandler} />
            )}
        </>
    )
}
